import { useRef } from 'react'

import type { QuoteCartQuote } from '@/components/quotes/quoteCartItems'
import { formatCurrency } from '@/lib/formatters'
import { CheckoutCartRow } from './CheckoutCartRow'
import { CHECKOUTS } from './checkoutData'
import { useViewportFillHeight } from './useViewportFillHeight'

const WHOLE_DOLLARS = { minimumFractionDigits: 0, maximumFractionDigits: 0 }

function isAwaitingPricing(carrierId: string) {
  return CHECKOUTS.some((checkout) => checkout.quote.id === carrierId && checkout.isCarrierEstimate)
}

function planFor(carrierId: string, title?: string) {
  return CHECKOUTS.find((checkout) => checkout.quote.id === carrierId)?.paymentPlans.find(
    (plan) => plan.title === title
  )
}

/** The Quotes page cart sidebar, rebuilt as a full-height right-hand column instead of a sheet. */
export function CheckoutCartPanel({
  groups,
  selectedPlans,
  onRemove,
}: {
  groups: QuoteCartQuote[]
  selectedPlans: Record<string, string>
  onRemove: (id: string) => void
}) {
  const asideRef = useRef<HTMLElement>(null)
  const height = useViewportFillHeight(asideRef)
  const isEmpty = groups.length === 0
  const chosenPlans = groups.flatMap((group) => {
    const plan = planFor(group.id, selectedPlans[group.id])
    return plan ? [plan] : []
  })
  // A chosen plan's own total carries its installment fees; before one is chosen, the quote's.
  const yearlyTotal = groups.reduce(
    (sum, group) => sum + (planFor(group.id, selectedPlans[group.id])?.total ?? group.subtotal),
    0
  )

  return (
    // top-18 sticks it under the product nav, which pins itself over the prototype frame's bar.
    <aside
      ref={asideRef}
      style={{ height }}
      className="border-ink-100 bg-background flex flex-col border-t lg:sticky lg:top-18 lg:w-[calc(28rem_+_var(--gutter))] lg:shrink-0 lg:border-t-0 lg:border-l"
    >
      {isEmpty ? (
        <div className="flex flex-1 items-center justify-center p-8">
          <p className="text-ink-400 max-w-60 text-center text-sm">
            No coverages yet. Open a quote and add the coverages you want.
          </p>
        </div>
      ) : (
        <ul className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 lg:pr-[calc(1.25rem_+_var(--gutter))]">
          {groups.map((group) => (
            <CheckoutCartRow
              key={group.id}
              quote={group}
              isCarrierEstimate={isAwaitingPricing(group.id)}
              firstPayment={planFor(group.id, selectedPlans[group.id])?.dueToday}
              onRemove={() => onRemove(group.id)}
            />
          ))}
        </ul>
      )}

      {!isEmpty && (
        <div className="border-ink-100 flex flex-col border-t">
          {chosenPlans.length > 0 && (
            <div className="bg-ink-50 border-ink-100 flex items-baseline justify-between gap-3 border-b px-5 py-4 lg:pr-[calc(1.25rem_+_var(--gutter))]">
              <span className="text-foreground font-semibold">First payments total</span>
              <span className="text-foreground text-xl font-extrabold">
                {formatCurrency(
                  chosenPlans.reduce((sum, plan) => sum + plan.dueToday, 0),
                  WHOLE_DOLLARS
                )}
              </span>
            </div>
          )}
          <div className="flex flex-col gap-3 p-5 lg:pr-[calc(1.25rem_+_var(--gutter))]">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="text-ink-500">Fees and taxes</span>
              <span className="text-foreground font-semibold">
                {formatCurrency(
                  groups.reduce(
                    (sum, group) => sum + group.oneTimeFees.reduce((fees, fee) => fees + fee.amount, 0),
                    0
                  ),
                  WHOLE_DOLLARS
                )}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-foreground font-semibold">Estimated total</span>
              <span>
                <span className="text-foreground text-xl font-extrabold">
                  {formatCurrency(yearlyTotal, WHOLE_DOLLARS)}
                </span>
                <span className="text-ink-500 text-sm"> / year</span>
              </span>
            </div>
          </div>
        </div>
      )}
    </aside>
  )
}
